import Axios, { AxiosRequestConfig } from 'axios';

export const AXIOS_INSTANCE = Axios.create({ baseURL: process.env.API_URL });

const dateTimeFormat =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

// Date-only strings (e.g. "2026-12-25") come from backend DateOnly fields.
const isoDateOnlyFormat = /^\d{4}-\d{2}-\d{2}$/;

// Function to check if a value is an ISO date-time string
function isIsoDateTimeString(value: any): boolean {
  return value && typeof value === 'string' && dateTimeFormat.test(value);
}

// Function to check if a value is a date-only string
function isIsoDateOnlyString(value: any): boolean {
  return value && typeof value === 'string' && isoDateOnlyFormat.test(value);
}

export function handleDates(body: any): void {
  if (body === null || body === undefined || typeof body !== 'object') return;

  for (const key of Object.keys(body)) {
    const value = body[key];
    // String-valued fields whose name ends in "String" (e.g. dateString, startString,
    // endString) are string representations the backend returns on purpose — their
    // declared type is `string`, and no Date-typed field is ever named this way. Never
    // coerce them to a Date even when the value looks like one ("2026-07-13" matches
    // isoDateOnlyFormat), otherwise a `string` field becomes a Date at runtime and
    // rendering it throws "Objects are not valid as a React child (found: [object Date])".
    // The `typeof value === 'string'` guard keeps this scoped to actual strings, so a
    // (hypothetical) object-valued field ending in "String" still falls through to the
    // recursion below.
    if (typeof value === 'string' && key.endsWith('String')) {
      continue;
    }
    if (isIsoDateTimeString(value)) {
      // eslint-disable-next-line no-param-reassign
      body[key] = new Date(value);
    } else if (isIsoDateOnlyString(value)) {
      // Parse as UTC midnight (what new Date("2026-12-25") does, and what the
      // NSwag client did). The Date must survive a fetch-modify-save round
      // trip: axios serializes Dates with toISOString(), and the backend's
      // DateOnlyJsonConverter takes the date part of that string. A
      // local-midnight parse reads back as the previous day for every
      // timezone east of UTC — that shifted holidays and price-list
      // activeFrom/activeTo one day per save. UTC midnight round-trips
      // exactly, and local rendering (formatDate & friends use local date
      // parts) still shows the right day at UTC+ offsets.
      // eslint-disable-next-line no-param-reassign
      body[key] = new Date(value);
    } else if (typeof value === 'object') {
      handleDates(value);
    }
  }
}

// The backend UrlEncodes fileDownloadName (see ExportController.GetFileResult),
// so after the header's own RFC 5987 percent-encoding is undone, one layer of
// percent-encoding remains and must be decoded too.
// Never throws — a malformed header must degrade to the fallback filename,
// not reject the download promise.
const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getFileNameFromContentDisposition = (
  header: string | undefined
): string | undefined => {
  if (!header) return undefined;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  const raw = utf8Match ? safeDecode(utf8Match[1]) : plainMatch?.[1];
  if (!raw) return undefined;
  return safeDecode(raw);
};

export const useCustomInstance = <T>(): ((
  config: AxiosRequestConfig
) => Promise<T>) => {
  return (config: AxiosRequestConfig) => {
    const source = Axios.CancelToken.source();
    const promise = AXIOS_INSTANCE({
      ...config,
      cancelToken: source.token,
    }).then(({ data, headers }) => {
      if (data instanceof Blob) {
        // Preserve the server's Content-Disposition filename by wrapping the
        // blob in a File, like NSwag's FileResponse.fileName used to carry it.
        const fileName = getFileNameFromContentDisposition(
          headers['content-disposition'] as string | undefined
        );
        return (
          fileName ? new File([data], fileName, { type: data.type }) : data
        ) as T;
      }
      handleDates(data); // because Orval doesn't convert dates
      return data;
    });

    // @ts-expect-error cancel is not part of the promise
    promise.cancel = () => {
      source.cancel('Query was cancelled by React Query');
    };

    return promise;
  };
};

export default useCustomInstance;
